import React, {MouseEvent, useState} from 'react';
import {useSelector} from 'react-redux';

import {getCancellationPolicyData} from '@ibibo/get-hotel-detail-page-data';
import {defaultTheme} from '@ibibo/build-theme';
import {apiConfig} from '@ibibo/hotels-config';

import * as S from './RoomSelection.styles';

import {RoomFlavorProps} from './Room';
import PayModePersuasionInfo from './PayAtHotelPersuasion/PayAtHotelPersuasion';

import {RoomDataType} from '../../redux/types';

import {numberOfNightsSelector} from '../../../common/redux/selectors';

import {detailsAPIEndPoints, __GOIBIBO__} from '../../../../config/api-config';
import {allFirstLevelObjectKeysAreNonNullable} from '../../../../utils/common-utils';

import TriggerAnchorText from '../../../../components/TriggerActionText';
import BrandModalHeader from '../../../../components/BrandModalHeader';
import Modal, {useModal} from '../../../../components/Modal';

import FreeCancelTick from '../../../../../stories/Icons/FreeCancelTick';
import RupeeIcon from '../../../../../stories/Icons/RupeeIcon';
import RoundedCheckboxIcon from '../../../../../stories/Icons/RoundedCheckbox';

type CancellationPolicyPropsTypes = {
  forwardParams: RoomFlavorProps['forwardParams'];
  roomPriceCode: RoomFlavorProps['roomPriceCode'];
  offercode: RoomFlavorProps['offercode'];
  vendor: RoomFlavorProps['vendor'];
  paymode: RoomFlavorProps['paymode'];
  roomTypeCode: RoomDataType['rtc'];
  checkinYYYYMMDD: string;
  checkoutYYYYMMDD: string;
  cityCode: string;
  hotelCode: string;
  paxString: string;
  gseg: RoomFlavorProps['gseg'];
  gsv: RoomFlavorProps['gsv'];
  cot: RoomFlavorProps['cot'];
};

const cancellationPolicyDefault: HotelCancellationPolicyDataType = {
  cancelPolicy: [],
  cancellation_text: []
};

const RoomFlavor: React.FC<{
  roomFlavor: Assign<RoomFlavorProps, {index: number; lastIndex: number}>;
  cancellationPolicyProps: CancellationPolicyPropsTypes;
  queryParamsForReviewUrlProps: QueryParamsForReviewUrlPropsTypes;
  noInclusionsAndSplInstructionsForAnyRoom: boolean;
  selectRoomClickHandler: void;
}> = ({
  roomFlavor,
  cancellationPolicyProps,
  selectRoomClickHandler,
}) => {
  const numberOfNights = useSelector(numberOfNightsSelector);
  const numberOfNightsText = `${numberOfNights} Night${numberOfNights > 1 ? 's' : ''}`;  

  const {
    refundStatusText,
    refundStatus,
    refundStatusTextColor,
    amenities = [],
    index,
    isRecommended,
    showSlashedPrice,
    slashedPrice,
    payAtPersuasionShowPrice,
    showPrice,
    tax,
    paymode,
    cot,
    roomGuestsString,
    cbo,
    gsv,
    gseg
  } = roomFlavor;

  const dis = (slashedPrice - showPrice);
  
  const [showAllAmeneties, setShowAllAmeneties] = useState(false);

  const showThisMuchAmenitiesUpfront = (amenities.length > 2 ? 1 : amenities.length);
  const firstDisplayAmenities = amenities.slice(0, showThisMuchAmenitiesUpfront);
  const remainingAmenities = amenities.slice(showThisMuchAmenitiesUpfront);


  const [cancellationPolicyData, setCancellationPolicyData] = useState<
    HotelCancellationPolicyDataType
  >(cancellationPolicyDefault);
  const [cancellationPolicyError, setCancellationPolicyError] = useState<Error | null>(null);

  const {
    isModalOpen: isCancellationPolicyModalOpen,
    openModal: openCancellationModal,
    closeModal: closeCancellationModal,
  } = useModal();

  const [gdsPolicy, setGdsPolicy] = useState(false);
  const [normalPolicy, setNormalPolicy] = useState(false);

  const showAllInclusions = () => {
    setShowAllAmeneties(!showAllAmeneties);
  }

  const openCancellation = async e => {
    openCancellationModal();

    const fetchData = await fetchCancellationPolicyData(e, cancellationPolicyProps);

    if (fetchData[0]?.cancelPolicy?.length || fetchData[0]?.cancellation_text?.length) {
      if(fetchData[0]?.cancelPolicy?.length){
        setGdsPolicy(false);
        setNormalPolicy(true);
      }
      if(fetchData[0]?.cancellation_text?.length){
        setGdsPolicy(true);
        setNormalPolicy(false);
      }
      setCancellationPolicyError(null);
      setCancellationPolicyData(fetchData[0]);
    }

    if (fetchData[1]) {
      setCancellationPolicyError(fetchData[1]);
      setGdsPolicy(false);
      setNormalPolicy(false);
    }
  }
  
  return (
    <>
    <S.RoomRatePriceInnerWrap>
      <S.RatePlanDetialWrap>
        <S.RatePlanInnerWrap>
          <S.RateOptionWrap>
            <S.RateOptionTagWrap>
              <S.RateOptionTagText>Option {index + 1}</S.RateOptionTagText>
              {isRecommended && <S.RecommendedTag>Recommended</S.RecommendedTag>}
            </S.RateOptionTagWrap>

            {(refundStatusText && refundStatus && <S.FreeCancelTag>
              <S.FreeCancelTagImgWrap>
                <FreeCancelTick width="1.9rem" height="2.4rem" />
              </S.FreeCancelTagImgWrap>
              <S.CancelInfoTextWrap>
                <S.FreeCancelText color={refundStatusTextColor}>{refundStatusText}</S.FreeCancelText>
                <TriggerAnchorText onClick={openCancellation}>
                  <S.CancelPolicyText>Cancellation Policy</S.CancelPolicyText>
                </TriggerAnchorText>
              </S.CancelInfoTextWrap>
            </S.FreeCancelTag>) || null}

            
            <S.RateOptionInclusionList>
              {(refundStatusText && !refundStatus &&
                <li className="nonRefund">Non-refundable <a onClick={openCancellation}>Cancellation Policy</a></li>
              ) || null}

              <PayModePersuasionInfo paymode={paymode} price={payAtPersuasionShowPrice} tax={tax} />

              {/* {firstDisplayAmenities && firstDisplayAmenities.map((amenity, i) => {
                if (i > 1) {
                  return null;
                }
                return (<li key={`${amenity}-${i}`}>
                  {amenity}
                </li>)
              })} */}
              
              {amenities && amenities.map((amenity, i) => {
                // if (i > 1) {
                //   return null;
                // }
                return (<li key={`${amenity}`}>
                  {amenity}
                </li>)
              })}


            </S.RateOptionInclusionList>

            {/* {(remainingAmenities && remainingAmenities.length && <S.MoreInclusionWrap onClick={showAllInclusions}>
              <S.MoreInclusionText>{showAllAmeneties ? '- less' : '+ more'} inclusions</S.MoreInclusionText>
            </S.MoreInclusionWrap>) || null} */}

          </S.RateOptionWrap>
        </S.RatePlanInnerWrap>
      </S.RatePlanDetialWrap>

      <S.RoomPriceDetailWrap>
        <S.RoomPriceInnerWrap>
          {(cot && cot === 'b2a' && <S.PartnerRateWrap>
            <S.PartnerRateText>Partner Exclusive Rate</S.PartnerRateText>
          </S.PartnerRateWrap>) || null}
          <S.PriceOptionWrap>
            <S.RoomPriceWrap>
              {(dis > 0 && showSlashedPrice && <S.OriginalPrice><RupeeIcon {...opriceRupeeIconProps} />{slashedPrice.toLocaleString('en-IN')}</S.OriginalPrice>) || null}
              <S.NewPrice><RupeeIcon {...spriceRupeeIconProps} />{showPrice.toLocaleString('en-IN')}</S.NewPrice>
              {(dis > 0 && showSlashedPrice && <S.DisPrice>(Discount <RupeeIcon {...dpriceRupeeIconProps} />{(slashedPrice - showPrice).toLocaleString('en-IN')})</S.DisPrice>) || null}
              {(cbo && cbo.map(item => 
                  <S.YouGetPriceText>Get&nbsp;<S.BoldText><RupeeIcon height="0.9rem" width="0.9rem" fill='#fff' />{item.cba}</S.BoldText>&nbsp;Promotional Discount</S.YouGetPriceText>)
                ) || null}
              <S.TotalGuestText>Total {roomGuestsString} / {numberOfNightsText}</S.TotalGuestText>
            </S.RoomPriceWrap>
            <S.RoomSelctBtnWrap>
              <S.RoomSelectBtn onClick={selectRoomClickHandler}>Select Room</S.RoomSelectBtn>
              {/* <S.LookingText>6 others looking this room right now!</S.LookingText> */}
            </S.RoomSelctBtnWrap>
          </S.PriceOptionWrap>
        </S.RoomPriceInnerWrap>
      </S.RoomPriceDetailWrap>
    </S.RoomRatePriceInnerWrap>
    <Modal
      isOpen={isCancellationPolicyModalOpen}
      closeModal={closeCancellationModal}
      modalStyles={{width: 'auto'}}
    >
      <BrandModalHeader>Cancellation Policy</BrandModalHeader>

      {normalPolicy && (
        <S.CancellationPolicyList>
          {cancellationPolicyData.cancelPolicy.map(item => (
            <S.CancellationPolicyListItem key={item}>
              <RoundedCheckboxIcon />
              <S.CancellationItemText>{item}</S.CancellationItemText>
            </S.CancellationPolicyListItem>
          ))}
        </S.CancellationPolicyList>
      ) || []}

      {gdsPolicy && (
          <S.CancellationPolicyList>
            {cancellationPolicyData.cancellation_text.map(item => (
              <S.CancellationPolicyListItem key={item}>
                <RoundedCheckboxIcon />
                <S.CancellationItemText>{item}</S.CancellationItemText>
              </S.CancellationPolicyListItem>
            ))}
          </S.CancellationPolicyList>
      ) || []}

      {!normalPolicy  && !gdsPolicy && !cancellationPolicyError && (
        <S.CancellationPolicyLoaderContainer>
          <S.CancellationPolicyListLoaderSmallItem></S.CancellationPolicyListLoaderSmallItem>
          <S.CancellationPolicyListLoaderLargeItem></S.CancellationPolicyListLoaderLargeItem>
          <S.CancellationPolicyListLoaderSmallItem></S.CancellationPolicyListLoaderSmallItem>
          <S.CancellationPolicyListLoaderLargeItem></S.CancellationPolicyListLoaderLargeItem>
        </S.CancellationPolicyLoaderContainer>
      )}

      {!!cancellationPolicyError && (
        <S.CancellationPolicyLoaderContainer>
          <S.CancellationPolicyErrorText>
            Something Went Wrong While Loading Cancellation Policy
          </S.CancellationPolicyErrorText>
        </S.CancellationPolicyLoaderContainer>
      )}
    </Modal>
    </>
  );
};

export default RoomFlavor;

const opriceRupeeIconProps = {
  fill: defaultTheme.colors.grey.light9b,
  width: '0.8rem',
  height: '0.8rem',
};

const spriceRupeeIconProps = {
  fill: defaultTheme.colors.grey.base,
  width: '1.5rem',
  height: '1.5rem',
};

const dpriceRupeeIconProps = {
  fill: defaultTheme.colors.red.base,
  width: '0.8rem',
  height: '0.8rem',
};

/**
 * the commented properties are not used
 */
type HotelCancellationPolicyDataType = {
  cancelPolicy?: string[];
  cancellation_text?: string[];
};

type FetchCancellationPolicyDataReturnType = [HotelCancellationPolicyDataType | null, Error | null];

async function fetchCancellationPolicyData(
  event: MouseEvent<HTMLSpanElement>,
  cancellationPolicyProps: CancellationPolicyPropsTypes,
) {
  event.preventDefault();

  const allCancellationPolicyPropsArePresent = allFirstLevelObjectKeysAreNonNullable(
    cancellationPolicyProps,
  );

  if (!allCancellationPolicyPropsArePresent) {
    return [cancellationPolicyDefault, null] as FetchCancellationPolicyDataReturnType;
  }

  const {
    hotelCode,
    roomTypeCode,
    roomPriceCode,
    offercode,
    forwardParams,
    vendor,
    cityCode,
    checkinYYYYMMDD,
    checkoutYYYYMMDD,
    paxString,
    paymode,
    gsv,
    gseg,
    cot
  } = cancellationPolicyProps;

  const cancellationPolicyQueryParams = {
    hotelCode: `hc=${hotelCode}`,
    roomTypeCode: `rtc=${roomTypeCode}`,
    roomPriceCode: `rpc=${encodeURIComponent(roomPriceCode)}`,
    offerCode: `offercode=${offercode}`,
    fwdParams: `fwdParams=${JSON.stringify(forwardParams)}`,
    vendor: `v=${vendor}`,
    sth: `strQry=hotels-${cityCode}-${checkinYYYYMMDD}-${checkoutYYYYMMDD}-${paxString}`,
    payMode: `pay_mode=${paymode}`,
    gseg: `segmentid=${gseg}`,
    gsv: `subvendor=${gsv}`,
    cot: `contracttype=${cot}`
  };

  const queryStringForCancellationUrl = Object.keys(cancellationPolicyQueryParams)
    .filter(sensibleQueryParamName => cancellationPolicyQueryParams[sensibleQueryParamName].length)
    .reduce((accumulatedQueryString, sensibleQueryParamName, index) => {
      const toAdd = cancellationPolicyQueryParams[sensibleQueryParamName];
      const prefix = index === 0 ? '?' : '&';

      return `${accumulatedQueryString}${prefix}${toAdd}`;
    }, '');

  const cancellationPolicyUrl = `${apiConfig.__MYPARTNER__}/${detailsAPIEndPoints.cancellationPolicy}/${queryStringForCancellationUrl}`;

  let hotelCancellationPolicyData: HotelCancellationPolicyDataType | null = null;
  let error = null;

  try {
    const response = await getCancellationPolicyData({url: cancellationPolicyUrl});
    hotelCancellationPolicyData = response.data;

  } catch (e) {
    error = e;
  }

  const ret: FetchCancellationPolicyDataReturnType = [hotelCancellationPolicyData, error];

  return ret;
}
